import React, { useState } from 'react';

// ELLai Logo Component — "Atelier" treatment
// Font: Bodoni Moda (400), loaded via <link> in public/index.html.
// Lowercase hairline didone: gold "ell" + teal "ai", both the same size.

const COLORS = {
  gold: '#B8860B',
  teal: '#1A6068',
  goldLight: '#D4A843',
  separator: '#d4c4a0',
};

const ELLaiLogo = ({
  size = 'large',       // 'small' | 'medium' | 'large' | 'icon'
  showTagline = true,    // hide tagline for compact uses
  showTree = true,       // text-only mode
  animated = false,      // roots-to-canopy growth animation (login/splash)
  className = '',
  style = {},
}) => {
  const sizes = {
    icon:   { tree: 60,  ell: 24, ai: 26, tag: 8,  sep: 60,  gap: 4  },
    small:  { tree: 100, ell: 36, ai: 40, tag: 10, sep: 100, gap: 6  },
    medium: { tree: 160, ell: 52, ai: 56, tag: 12, sep: 140, gap: 10 },
    large:  { tree: 200, ell: 72, ai: 78, tag: 15, sep: 180, gap: 12 },
  };

  const s = sizes[size] || sizes.large;

  const containerStyle = {
    display: 'inline-flex',
    flexDirection: 'column',
    alignItems: 'center',
    background: 'transparent',
    ...style,
  };

  const treeStyle = {
    width: s.tree,
    height: 'auto',
    marginBottom: s.gap,
  };

  const nameStyle = {
    fontSize: s.ell,
    lineHeight: 1,
    marginBottom: 2,
  };

  // Colors resolve through CSS vars so alternate themes (e.g. the
  // /preview/earth palette test) can restyle the wordmark from CSS.
  const ellStyle = {
    fontFamily: "'Bodoni Moda', 'Bodoni 72', Didot, Georgia, serif",
    fontWeight: 500,
    fontOpticalSizing: 'none',
    fontVariationSettings: '"opsz" 22',
    color: `var(--ellai-ell, ${COLORS.gold})`,
    letterSpacing: '0.005em',
    textTransform: 'lowercase',
  };

  const aiStyle = {
    fontFamily: "'Bodoni Moda', 'Bodoni 72', Didot, Georgia, serif",
    fontWeight: 500,
    fontOpticalSizing: 'none',
    fontVariationSettings: '"opsz" 22',
    color: `var(--ellai-ai, ${COLORS.teal})`,
    letterSpacing: '0.005em',
    textTransform: 'lowercase',
  };

  const separatorStyle = {
    width: s.sep,
    height: 1,
    background: `linear-gradient(to right, transparent, ${COLORS.separator}, transparent)`,
    margin: `${s.gap / 2}px 0`,
  };

  const taglineStyle = {
    fontFamily: "'Bodoni Moda', 'Bodoni 72', Didot, Georgia, serif",
    fontSize: s.tag,
    letterSpacing: '0.35em',
    color: 'var(--ellai-tag, #3a362f)',
    fontWeight: 500,
    textTransform: 'uppercase',
  };

  return (
    <div style={containerStyle} className={className}>
      {showTree && (
        animated ? (
          <GrowthVideo size={s.tree} gap={s.gap} />
        ) : (
          <img src="/ellai-tree.png?v=4" alt="ELLai" style={treeStyle} />
        )
      )}
      <div style={nameStyle} className={animated ? 'ellai-name-rise' : ''}>
        <span style={ellStyle}>ELL</span>
        <span style={aiStyle}>ai</span>
      </div>
      {showTagline && size !== 'icon' && (
        <>
          <div style={separatorStyle} className={animated ? 'ellai-sep-draw' : ''} />
          <div style={taglineStyle} className={animated ? 'ellai-tagline-rise' : ''}>Eternal Living Legacy</div>
        </>
      )}
    </div>
  );
};

// Seedling-to-tree growth film (AI-generated from the real artwork),
// resolving into the true logo once it ends. Falls back to the static
// logo on error or reduced-motion.
const GrowthVideo = ({ size, gap }) => {
  const reduceMotion = typeof window !== 'undefined'
    && window.matchMedia
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const [done, setDone] = useState(reduceMotion);

  const feather = 'radial-gradient(closest-side, black 72%, transparent 99%)';
  return (
    <div style={{ position: 'relative', width: size * 1.25, aspectRatio: '1 / 1', marginBottom: gap, overflow: 'hidden' }}>
      {!reduceMotion && (
        <video
          src="/ellai-grow.mp4"
          autoPlay
          muted
          playsInline
          preload="auto"
          onEnded={() => setDone(true)}
          onError={() => setDone(true)}
          style={{
            // Scaled so the film's tree matches the logo's rendered size —
            // measured from the final frame: tree = 64.4% of video width,
            // target = 82% of box → 1.272x, re-centered on the tree
            position: 'absolute', width: '127.2%', height: '127.2%',
            left: '-12.9%', top: '-14.2%',
            objectFit: 'cover', pointerEvents: 'none',
            WebkitMaskImage: feather, maskImage: feather,
            opacity: done ? 0 : 1,
            transition: 'opacity 0.7s ease',
          }}
        />
      )}
      <img
        src="/ellai-tree.png?v=4"
        alt="ELLai"
        style={{
          position: 'absolute', inset: '9%', width: '82%', height: '82%',
          objectFit: 'contain',
          opacity: done ? 1 : 0,
          transition: 'opacity 0.7s ease',
        }}
      />
    </div>
  );
};

export default ELLaiLogo;
